import React, { useEffect, useState } from 'react';
import { Form, Input, DatePicker, Select, Button, Spin } from 'antd';
import dayjs from 'dayjs';
import { useAuth0 } from '@auth0/auth0-react';
import { PageTitle, StandardCard } from '../common';
import { theme } from '../../theme';

const { Option } = Select;

const motivoOptions = [
  { value: 'R', label: 'Recepcion' },
  { value: 'D', label: 'Despacho' },
];

const formItemStyle = {
  display: 'flex',
  alignItems: 'center',
  marginBottom: 12,
};
const labelStyle = {
  minWidth: 100,
  marginRight: 12,
  fontWeight: 500,
  fontSize: '13px',
};

const RecepcionDespacho = () => {
  const { getAccessTokenSilently, user } = useAuth0();
  const [form] = Form.useForm();
  const [productos, setProductos] = useState([]);
  const [loadingProductos, setLoadingProductos] = useState(false);
  const [ordenesPendientes, setOrdenesPendientes] = useState<any[]>([]);
  const [loadingOrdenes, setLoadingOrdenes] = useState(false);
  const [ordenSeleccionada, setOrdenSeleccionada] = useState<any | null>(null);
  const [ticketsBalanza, setTicketsBalanza] = useState<any[]>([]);
  const [loadingTickets, setLoadingTickets] = useState(false);
  const [ticketSeleccionado, setTicketSeleccionado] = useState<any | null>(null);
  const [validandoOrden, setValidandoOrden] = useState(false);
  const [validandoTicket, setValidandoTicket] = useState(false);

  useEffect(() => {
    const fetchProductos = async () => {
      setLoadingProductos(true);
      try {
        const token = await getAccessTokenSilently();
        const response = await fetch(`${process.env.REACT_APP_BALANZA_URL}/productos/`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        if (!response.ok) throw new Error('Error al cargar productos');
        const data = await response.json();
        // Filtrar productos excluyendo tipo=1 (mostrar solo tipo!=1)
        const productosFiltrados = data.filter((producto: any) => producto.tipo !== 1);
        setProductos(productosFiltrados);
        
        // Si hay productos, establecer el primer producto por defecto pero NO cargar órdenes
        if (productosFiltrados.length > 0) {
          const primerProducto = productosFiltrados[0];
          form.setFieldsValue({ producto: primerProducto.id });
          // NO cargar órdenes automáticamente al inicio
        }
      } catch (error) {
        setProductos([]);
      } finally {
        setLoadingProductos(false);
      }
    };
    fetchProductos();
  }, [getAccessTokenSilently]);

    // Función para validar una orden específica por número
  const validarOrden = async (numeroOrden: string) => {
    if (!user?.id_empresa || !numeroOrden) return;
    
    setValidandoOrden(true);
    try {
      const motivo = form.getFieldValue('motivo');
      const productoSeleccionado = form.getFieldValue('producto');
      
      // Usar la función cargarOrdenesPendientes con el número de orden específico
      await cargarOrdenesPendientes(motivo, productoSeleccionado, numeroOrden);
      
    } catch (error) {
      console.error('Error al validar orden:', error);
      alert('Error al validar la orden. Verifique el número ingresado.');
      setOrdenSeleccionada(null);
      setTicketsBalanza([]);
    } finally {
      setValidandoOrden(false);
    }
  };

  // Función para cargar órdenes pendientes según el tipo y producto seleccionados (mantenida para compatibilidad)
  const cargarOrdenesPendientes = async (tipo: string, idProducto: string, numeroOrden?: string) => {
    if (!user?.id_empresa || !idProducto) return;
    
    setLoadingOrdenes(true);
    try {
      const token = await getAccessTokenSilently();
      
             // Construir la URL según si se está validando una orden específica o cargando todas
       let url = `${process.env.REACT_APP_ORDENES_URL}/ordenes_pendientes_recepcion_despacho/${user.id_empresa}/${user.id_sucursal}/${tipo}/${idProducto}/`;
       if (numeroOrden) {
         url += `${numeroOrden}/`;
       }
      
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        if (response.status === 404) {
          alert('Orden no encontrada o no válida para recepción/despacho');
          setOrdenSeleccionada(null);
          setTicketsBalanza([]);
          return;
        }
        
        // Intentar obtener el mensaje de error específico del servidor
        try {
          const errorData = await response.json();
          if (errorData.error) {
            alert(errorData.error);
          } else {
            alert('Error al cargar órdenes pendientes');
          }
        } catch {
          alert('Error al cargar órdenes pendientes');
        }
        
        setOrdenSeleccionada(null);
        setTicketsBalanza([]);
        return;
      }
      
      const data = await response.json();
      console.log('ordenes pendientes', data);
      
      if (numeroOrden) {
        // Si se está validando una orden específica, buscar esa orden en los datos
        const ordenEncontrada = data.find((orden: any) => orden.nro_orden == numeroOrden);
        if (ordenEncontrada) {
          setOrdenSeleccionada(ordenEncontrada);
          // Cargar tickets para esta orden específica
          cargarTicketsPendientes(ordenEncontrada);
        } else {
          alert('Orden no encontrada');
          setOrdenSeleccionada(null);
          setTicketsBalanza([]);
        }
      } else {
        // Si se están cargando todas las órdenes
        setOrdenesPendientes(data);
        
        // Cargar automáticamente los tickets pendientes después de cargar las órdenes
        if (data.length > 0) {
          console.log('Órdenes cargadas, iniciando carga de tickets...');
          setTimeout(() => {
            cargarTicketsPendientesParaTodas();
          }, 100);
        } else {
          console.log('No se encontraron órdenes pendientes');
          setTicketsBalanza([]);
        }
      }
    } catch (error) {
      console.error('Error al cargar órdenes pendientes:', error);
      setOrdenesPendientes([]);
      setOrdenSeleccionada(null);
      setTicketsBalanza([]);
    } finally {
      setLoadingOrdenes(false);
    }
  };

  // Cargar órdenes cuando cambie el tipo de motivo
  const handleMotivoChange = (tipo: string) => {
    // Limpiar orden seleccionada cuando cambie el motivo
    setOrdenSeleccionada(null);
    setTicketsBalanza([]);
    form.setFieldsValue({ orden_pendiente: undefined });
  };

  // Cargar órdenes cuando cambie el producto
  const handleProductoChange = (idProducto: string) => {
    // Limpiar orden seleccionada cuando cambie el producto
    setOrdenSeleccionada(null);
    setTicketsBalanza([]);
    form.setFieldsValue({ orden_pendiente: undefined });
  };

  // Función para cargar tickets pendientes para todas las órdenes
  const cargarTicketsPendientesParaTodas = async () => {
    if (!user?.id_empresa || ordenesPendientes.length === 0) {
      console.log('No hay órdenes pendientes para cargar tickets');
      return;
    }
    
    setLoadingTickets(true);
    try {
      const token = await getAccessTokenSilently();
      const motivo = form.getFieldValue('motivo');
      const ingresoEgreso = motivo === 'R' ? 'I' : 'E'; // R=Recepción=I, D=Despacho=E
      const productoSeleccionado = form.getFieldValue('producto');
      
             // Obtener todos los clientes únicos de las órdenes
       const clientesUnicos = new Set();
       ordenesPendientes.forEach(orden => {
         let idCliente;
         if (orden.tipo === 'C') {
           idCliente = orden.contrato_vendedor_id;
         } else if (orden.tipo === 'V') {
           // Para tipo V (Venta): usar comprador_id (Ingreso A debe ser el comprador del contrato)
           idCliente = orden.contrato_comprador_id;
                   } else if (orden.tipo === 'A') {
            idCliente = orden.sucursal_otra_id || orden.destino_id;
                   } else if (orden.tipo === 'E') {
            // Para tipo E (Entrega a Cliente): usar origen_id (el cliente que aparece en "Salida desde")
            idCliente = orden.origen_id || orden.cliente_id || orden.destino_id || orden.empresa_id;
          } else if (orden.tipo === 'D') {
            // Para tipo D (Cambio Dueño Salida): usar la misma estrategia que tipo E
            idCliente = orden.origen_id || orden.cliente_id || orden.destino_id || orden.empresa_id;
          } else {
           idCliente = orden.cliente_id || orden.contrato_comprador_id || orden.contrato_vendedor_id;
         }
         
         if (!idCliente && orden.destino_id) {
           idCliente = orden.destino_id;
         }
         
         if (idCliente) {
           clientesUnicos.add(idCliente);
         }
       });

             // Cargar tickets para todos los clientes únicos
       const todosLosTickets = [];
       const clientesArray = Array.from(clientesUnicos);
       for (const idCliente of clientesArray) {
         try {
                       // Buscar órdenes que correspondan a este cliente para obtener el tipo correcto
            const ordenesDelCliente = ordenesPendientes.filter(orden => {
              let ordenCliente;
              if (orden.tipo === 'C') {
                ordenCliente = orden.contrato_vendedor_id;
              } else if (orden.tipo === 'V') {
                // Para tipo V (Venta): usar comprador_id (Ingreso A debe ser el comprador del contrato)
                ordenCliente = orden.contrato_comprador_id;
                             } else if (orden.tipo === 'A') {
                 ordenCliente = orden.sucursal_otra_id || orden.destino_id;
                             } else if (orden.tipo === 'E') {
                 // Para tipo E (Entrega a Cliente): usar origen_id (el cliente que aparece en "Salida desde")
                 ordenCliente = orden.origen_id || orden.cliente_id || orden.destino_id || orden.empresa_id;
               } else if (orden.tipo === 'D') {
                 // Para tipo D (Cambio Dueño Salida): usar la misma estrategia que tipo E
                 ordenCliente = orden.origen_id || orden.cliente_id || orden.destino_id || orden.empresa_id;
               } else {
                ordenCliente = orden.cliente_id || orden.contrato_comprador_id || orden.contrato_vendedor_id;
              }
              
              if (!ordenCliente && orden.destino_id) {
                ordenCliente = orden.destino_id;
              }
              
              return ordenCliente === idCliente;
            });
           
           // Usar el tipo de la primera orden encontrada para este cliente
           const tipoOrden = ordenesDelCliente.length > 0 ? ordenesDelCliente[0].tipo : 'V';
           
           const url = `${process.env.REACT_APP_ORDENES_URL}/tickets_pendientes_recepcion_despacho/${user.id_empresa}/${ingresoEgreso}/${productoSeleccionado}/${tipoOrden}/${idCliente}/`;
           console.log(`Cargando tickets para cliente ${idCliente}, tipo ${tipoOrden}:`, url);
           
           const response = await fetch(url, {
             method: 'GET',
             headers: {
               'Authorization': `Bearer ${token}`,
               'Content-Type': 'application/json'
             }
           });
           if (response.ok) {
             const data = await response.json();
             todosLosTickets.push(...data);
           }
         } catch (error) {
           console.error(`Error al cargar tickets para cliente ${idCliente}:`, error);
         }
       }

      // Eliminar duplicados por ID
      const ticketsUnicos = todosLosTickets.filter((ticket, index, self) => 
        index === self.findIndex(t => t.id === ticket.id)
      );
      
      console.log('todos los tickets pendientes', ticketsUnicos);
      setTicketsBalanza(ticketsUnicos);
    } catch (error) {
      console.error('Error al cargar tickets pendientes:', error);
      setTicketsBalanza([]);
    } finally {
      setLoadingTickets(false);
    }
  };

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (values: any) => {
    // Validar que el usuario tenga empresa
    if (!user?.id_empresa) {
      console.error('Usuario sin empresa asignada');
      alert('Error: Usuario sin empresa asignada');
      return;
    }

    // Validar que se haya ingresado un número de orden
    const numeroOrden = values.orden_pendiente;
    if (!numeroOrden) {
      alert('Por favor, ingrese un número de orden válido');
      return;
    }

    // Validar que se haya ingresado un número de ticket
    const numeroTicket = values.ticket_balanza;
    if (!numeroTicket) {
      alert('Por favor, ingrese un número de ticket válido');
      return;
    }

    // Validar que se haya validado y seleccionado una orden
    if (!ordenSeleccionada) {
      alert('Por favor, valide el número de orden antes de grabar');
      return;
    }

    // Validar que se haya validado y seleccionado un ticket
    if (!ticketSeleccionado) {
      alert('Por favor, valide el número de ticket antes de grabar');
      return;
    }

    // Validar que el número de orden ingresado coincida con el validado
    if (ordenSeleccionada.nro_orden != numeroOrden) {
      alert('El número de orden ingresado no coincide con el validado. Por favor, valide nuevamente.');
      return;
    }

    // Validar que el número de ticket ingresado coincida con el validado
    if (ticketSeleccionado.numero_ticket != numeroTicket) {
      alert('El número de ticket ingresado no coincide con el validado. Por favor, valide nuevamente.');
      return;
    }

    // Prevenir múltiples envíos
    if (isSubmitting) {
      console.log('Ya se está procesando una solicitud...');
      return;
    }

    setIsSubmitting(true);

    try {
      console.log('Iniciando proceso de grabación...');
      
      const token = await getAccessTokenSilently();
      console.log('Token obtenido, preparando datos...');
      
      // Preparar datos para enviar
      const datosEnvio = {
        fecha: values.fecha.format('YYYY-MM-DD'),
        motivo: values.motivo,
        marca: values.marca || '',
        buque: values.buque || '',
        observaciones: values.observaciones || ''
      };

      console.log('Datos a enviar:', datosEnvio);
      
      const url = `${process.env.REACT_APP_ORDENES_URL}/crear_recepcion_despacho/${user.id_empresa}/${ordenSeleccionada.id}/${ticketSeleccionado.id}/`;
      console.log('URL de destino:', url);

      console.log('Enviando petición al servidor...');
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(datosEnvio)
      });

      console.log('Respuesta recibida, status:', response.status);

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `Error del servidor: ${response.status}`);
      }

      const result = await response.json();
      console.log('Proceso completado exitosamente:', result);
      
      // Mostrar mensaje de éxito
      alert(`Recepción/Despacho creado exitosamente\nID: ${result.id}\nNúmero: ${result.nro_recdes}`);
      
      // Resetear el formulario
      form.resetFields();
      form.setFieldsValue({ fecha: dayjs() });
      
      // Limpiar los datos desplegados
      setOrdenSeleccionada(null);
      setTicketSeleccionado(null);
      
      // Limpiar las listas de órdenes y tickets
      setOrdenesPendientes([]);
      setTicketsBalanza([]);
      
      // Limpiar el estado de validación
      setValidandoOrden(false);
      setValidandoTicket(false);
      
    } catch (error: any) {
      console.error('Error durante el proceso:', error);
      alert(`Error: ${error.message || 'Error desconocido'}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Placeholder para botones 'capturar'
  const handleCapturar = (campo: string) => {
    // Aquí iría la lógica de captura (ej: abrir modal, escanear, etc)
    console.log(`Capturar para campo: ${campo}`);
  };

  // Función para cargar tickets pendientes cuando se seleccione una orden
  const cargarTicketsPendientes = async (orden: any) => {
    console.log('cargarTicketsPendientes iniciada con orden:', orden);
    if (!user?.id_empresa || !orden) {
      console.log('No se puede cargar tickets - faltan datos:', { user: user?.id_empresa, orden });
      return;
    }
    
         console.log('Orden seleccionada completa:', orden);
     console.log('Tipo de orden:', orden.tipo);
     console.log('vendedor_id:', orden.contrato_vendedor_id);
     console.log('comprador_id:', orden.contrato_comprador_id);
     console.log('sucursal_otra_id:', orden.sucursal_otra_id);
     console.log('cliente_id:', orden.cliente_id);
     console.log('destino_id:', orden.destino_id);
     console.log('origen_id:', orden.origen_id);
     console.log('empresa_id:', orden.empresa_id);
     console.log('Todos los campos de la orden:', Object.keys(orden));
    
    setLoadingTickets(true);
    try {
      const token = await getAccessTokenSilently();
      
      // Determinar el id_cliente según el tipo de orden
      let idCliente;
      if (orden.tipo === 'C') {
        // Para tipo C (Compra): usar vendedor_id
        idCliente = orden.contrato_vendedor_id;
      } else if (orden.tipo === 'V') {
        // Para tipo V (Venta): usar comprador_id (Ingreso A debe ser el comprador del contrato)
        idCliente = orden.contrato_comprador_id;
      } else if (orden.tipo === 'A') {
        console.log('Paso x aqui 1',orden.tipo);
// Para tipo A (Otra Sucursal): usar sucursal_otra_id, con fallback a destino_id
        idCliente = orden.sucursal_otra_id || orden.destino_id;
      } else if (orden.tipo === 'E') {
        // Para tipo E (Entrega a Cliente): usar origen_id (el cliente que aparece en "Salida desde")
        idCliente =  orden.cliente;
        console.log('Para orden tipo E, cliente_id determinado:', idCliente, 'de campos:', {
  
        });
      } else if (orden.tipo === 'D') {
        // Para tipo D (Cambio Dueño Salida): usar la misma estrategia que tipo E
        idCliente = orden.cliente;
        console.log('Para orden tipo D, cliente_id determinado:', idCliente, 'de campos:', {
  
        });
      } else {
        console.log('Paso x aqui 2');
        // Para otros tipos (F, D): usar cliente_id como fallback
        idCliente = orden.cliente_id || orden.contrato_comprador_id || orden.contrato_vendedor_id;
      }
      
      // Si idCliente es nulo, usar idDestino como fallback
      if (!idCliente && orden.destino_id) {
        idCliente = orden.destino_id;
        console.log('idCliente es nulo, usando idDestino:', idCliente);
      }
      
      // Validar que tengamos todos los datos necesarios
      if (!idCliente&& orden.tipo !== 'A') {
        console.error('No se pudo determinar el id_cliente para la orden:', orden);
        alert('Error: No se pudo determinar el cliente para esta orden. Verifique los datos de la orden.');
        setTicketsBalanza([]);
        return;
      }
      
             // Verificar si tenemos el producto_id o el producto
       const productoId = orden.producto_id || orden.producto;
       if (!productoId) {
         console.error('La orden no tiene producto_id ni producto:', orden);
         alert('Error: La orden seleccionada no tiene un producto válido.');
         setTicketsBalanza([]);
         return;
       }
      
             console.log('id_cliente determinado:', idCliente);
       console.log('producto_id:', productoId);
       console.log('Tipo de orden procesado:', orden.tipo);
      
      // Determinar ingreso/egreso según el motivo seleccionado
      const motivo = form.getFieldValue('motivo');
      const ingresoEgreso = motivo === 'R' ? 'I' : 'E'; // R=Recepción=I, D=Despacho=E
      
             const url = `${process.env.REACT_APP_ORDENES_URL}/tickets_pendientes_recepcion_despacho/${user.id_empresa}/${ingresoEgreso}/${productoId}/${orden.tipo}/${idCliente}/`;
      console.log('URL a llamar:', url);
      
      console.log('Haciendo petición a:', url);
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      console.log('Respuesta del servidor - Status:', response.status);
      console.log('Respuesta del servidor - OK:', response.ok);
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('Error response:', errorText);
        throw new Error(`Error al cargar tickets pendientes: ${response.status} - ${errorText}`);
      }
      
      const data = await response.json();
      console.log('tickets pendientes recibidos:', data);
      console.log('Cantidad de tickets:', data.length);
      setTicketsBalanza(data);
    } catch (error) {
      console.error('Error al cargar tickets pendientes:', error);
      setTicketsBalanza([]);
    } finally {
      setLoadingTickets(false);
    }
  };

  const handleOrdenChange = (ordenId: string) => {
    console.log('handleOrdenChange llamado con ordenId:', ordenId);
    const orden = ordenesPendientes.find(o => o.id === ordenId || o.id === Number(ordenId));
    console.log('Orden encontrada:', orden);
    setOrdenSeleccionada(orden || null);
    if (orden) {
      // Autocompletar campos del formulario si existen en la orden
      const valuesToSet: any = {};
      if (orden.producto) valuesToSet.producto = orden.producto;
      if (orden.marca) valuesToSet.marca = orden.marca;
      if (orden.buque) valuesToSet.buque = orden.buque;
      // Puedes agregar más campos aquí si lo deseas
      form.setFieldsValue(valuesToSet);
      
      // Cargar tickets específicos para esta orden
      console.log('Llamando a cargarTicketsPendientes con orden:', orden);
      cargarTicketsPendientes(orden);
    }
  };

  // Función para manejar el cambio en el input de número de orden
  const handleNumeroOrdenChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const numeroOrden = e.target.value;
    // Limpiar si el campo está vacío
    if (!numeroOrden) {
      setOrdenSeleccionada(null);
      setTicketsBalanza([]);
    }
  };

  // Función para manejar el lost focus del input de número de orden
  const handleNumeroOrdenBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const numeroOrden = e.target.value;
    if (numeroOrden) { // Validar siempre que haya un valor
      validarOrden(numeroOrden);
    }
  };



  // Función para manejar el cambio en el input de número de ticket
  const handleNumeroTicketChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const numeroTicket = e.target.value;
    // Limpiar si el campo está vacío
    if (!numeroTicket) {
      setTicketSeleccionado(null);
    }
  };

  // Función para validar un ticket específico por número
  const validarTicket = async (numeroTicket: string) => {
    if (!user?.id_empresa || !numeroTicket || !ordenSeleccionada) return;
    
    setValidandoTicket(true);
    try {
      const token = await getAccessTokenSilently();
      const motivo = form.getFieldValue('motivo');
      const productoSeleccionado = form.getFieldValue('producto');
      
             // Lógica especial para órdenes tipo E
       if (ordenSeleccionada.tipo === 'E') {
         // Para tipo E: usar origen_id (el cliente que aparece en "Salida desde") para buscar tickets
        const clienteId =  ordenSeleccionada.cliente;
        if (!clienteId) {
          alert(`No se pudo determinar el cliente para esta orden tipo E. Campos disponibles: ${JSON.stringify({
            origen_id: ordenSeleccionada.origen_id,
            cliente_id: ordenSeleccionada.cliente_id,
            destino_id: ordenSeleccionada.destino_id,
            empresa_id: ordenSeleccionada.empresa_id
          })}`);
          setTicketSeleccionado(null);
          return;
        }
        
        const url = `${process.env.REACT_APP_ORDENES_URL}/tickets_pendientes_recepcion_despacho/${user.id_empresa}/${user.id_sucursal}/${motivo}/${productoSeleccionado}/E/${clienteId}/`;
        
        const response = await fetch(url, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        
        if (!response.ok) {
          if (response.status === 404) {
            alert('Ticket no encontrado o no válido para esta orden');
            setTicketSeleccionado(null);
            return;
          }
          throw new Error('Error al validar el ticket');
        }
        
        const tickets = await response.json();
        console.log('Tickets disponibles para tipo E:', tickets);
        
                 // Filtrar tickets según los criterios específicos para tipo E
         const ticketsFiltrados = tickets.filter((ticket: any) => {
           // 1. Tipo ticket balanza == tipo orden
           const tipoCoincide = ticket.compra_servicio === ordenSeleccionada.tipo;
           
           // 2. Producto ticket balanza == producto orden
           const productoCoincide = ticket.producto_id === ordenSeleccionada.producto_id || 
                                   ticket.producto === ordenSeleccionada.producto_id;
           
           // 3. Cliente balanza == cliente orden
           const clienteCoincide = ticket.cliente_id === ordenSeleccionada.cliente_id;
           
           return tipoCoincide && productoCoincide && clienteCoincide;
         });
        
        console.log('Tickets filtrados para tipo E:', ticketsFiltrados);
        
        // Buscar el ticket específico por número en los tickets filtrados
        const ticketEncontrado = ticketsFiltrados.find((ticket: any) => ticket.numero_ticket == numeroTicket);
        
        if (ticketEncontrado) {
          setTicketSeleccionado(ticketEncontrado);
          console.log('Ticket validado para tipo E:', ticketEncontrado);
        } else {
          alert('Ticket no encontrado en la lista de tickets válidos para esta orden tipo E');
          setTicketSeleccionado(null);
        }
        
      } else if (ordenSeleccionada.tipo === 'D') {
        // Para tipo D: usar la misma estrategia que tipo E
        const clienteId = ordenSeleccionada.cliente;
        if (!clienteId) {
          alert(`No se pudo determinar el cliente para esta orden tipo D. Campos disponibles: ${JSON.stringify({
            origen_id: ordenSeleccionada.origen_id,
            cliente_id: ordenSeleccionada.cliente_id,
            destino_id: ordenSeleccionada.destino_id,
            empresa_id: ordenSeleccionada.empresa_id
          })}`);
          setTicketSeleccionado(null);
          return;
        }
        
        const url = `${process.env.REACT_APP_ORDENES_URL}/tickets_pendientes_recepcion_despacho/${user.id_empresa}/${user.id_sucursal}/${motivo}/${productoSeleccionado}/D/${clienteId}/`;
        
        const response = await fetch(url, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        
        if (!response.ok) {
          if (response.status === 404) {
            alert('Ticket no encontrado o no válido para esta orden');
            setTicketSeleccionado(null);
            return;
          }
          throw new Error('Error al validar el ticket');
        }
        
        const tickets = await response.json();
        console.log('Tickets disponibles para tipo D:', tickets);
        
        // Filtrar tickets según los criterios específicos para tipo D (misma lógica que tipo E)
        const ticketsFiltrados = tickets.filter((ticket: any) => {
          // 1. Tipo ticket balanza == tipo orden
          const tipoCoincide = ticket.compra_servicio === ordenSeleccionada.tipo;
          
          // 2. Producto ticket balanza == producto orden
          const productoCoincide = ticket.producto_id === ordenSeleccionada.producto_id || 
                                  ticket.producto === ordenSeleccionada.producto_id;
          
          // 3. Cliente balanza == cliente orden
          const clienteCoincide = ticket.cliente_id === ordenSeleccionada.cliente_id;
          
          return tipoCoincide && productoCoincide && clienteCoincide;
        });
        
        console.log('Tickets filtrados para tipo D:', ticketsFiltrados);
        
        // Buscar el ticket específico por número en los tickets filtrados
        const ticketEncontrado = ticketsFiltrados.find((ticket: any) => ticket.numero_ticket == numeroTicket);
        
        if (ticketEncontrado) {
          setTicketSeleccionado(ticketEncontrado);
          console.log('Ticket validado para tipo D:', ticketEncontrado);
        } else {
          alert('Ticket no encontrado en la lista de tickets válidos para esta orden tipo D');
          setTicketSeleccionado(null);
        }
        
      } else {
        // Lógica original para otros tipos de orden
        // Determinar el origen_id según el tipo de orden
        let origenId;
        if (ordenSeleccionada.tipo === 'C') {
          // Para tipo C (Compra): usar vendedor_id
          origenId = ordenSeleccionada.contrato_vendedor_id;
        } else if (ordenSeleccionada.tipo === 'V') {
          // Para tipo V (Venta): usar comprador_id (Ingreso A debe ser el comprador del contrato)
          origenId = ordenSeleccionada.contrato_comprador_id;
        } else if (ordenSeleccionada.tipo === 'A') {
          console.log('Paso x aqui 3',ordenSeleccionada.sucursal_otra,ordenSeleccionada.destino_id);
          // Para tipo A (Otra Sucursal): usar sucursal_otra_id, con fallback a destino_id
          origenId = ordenSeleccionada.sucursal_otra || ordenSeleccionada.destino_id;
        } else {
          // Para otros tipos: usar cliente_id como fallback
          origenId = ordenSeleccionada.cliente_id || ordenSeleccionada.contrato_comprador_id || ordenSeleccionada.contrato_vendedor_id;
        }
        
        if (!origenId) {
          alert('No se pudo determinar el origen para validar el ticket');
          setTicketSeleccionado(null);
          return;
        }
        
        const url = `${process.env.REACT_APP_ORDENES_URL}/tickets_pendientes_recepcion_despacho/${user.id_empresa}/${user.id_sucursal}/${motivo}/${productoSeleccionado}/${ordenSeleccionada.tipo}/${origenId}/`;
        
        const response = await fetch(url, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        
        if (!response.ok) {
          if (response.status === 404) {
            alert('Ticket no encontrado o no válido para esta orden');
            setTicketSeleccionado(null);
            return;
          }
          throw new Error('Error al validar el ticket');
        }
        
        const tickets = await response.json();
        console.log('Tickets disponibles:', tickets);
        
        // Buscar el ticket específico por número
        const ticketEncontrado = tickets.find((ticket: any) => ticket.numero_ticket == numeroTicket);
        
        if (ticketEncontrado) {
          setTicketSeleccionado(ticketEncontrado);
          console.log('Ticket validado:', ticketEncontrado);
        } else {
          alert('Ticket no encontrado en la lista de tickets válidos para esta orden');
          setTicketSeleccionado(null);
        }
      }
      
    } catch (error) {
      console.error('Error al validar ticket:', error);
      alert('Error al validar el ticket. Verifique el número ingresado.');
      setTicketSeleccionado(null);
    } finally {
      setValidandoTicket(false);
    }
  };

  // Función para manejar el lost focus del input de número de ticket
  const handleNumeroTicketBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const numeroTicket = e.target.value;
    if (numeroTicket && ordenSeleccionada) {
      validarTicket(numeroTicket);
    } else if (!ordenSeleccionada) {
      alert('Debe seleccionar una orden antes de validar el ticket');
    }
  };

  const handleTicketChange = (ticketId: string) => {
    const ticket = ticketsBalanza.find(t => t.id === ticketId || t.id === Number(ticketId));
    setTicketSeleccionado(ticket || null);
  };

  return (
    <div style={{ padding: '0', width: '100%', maxWidth: '900px', margin: '0 auto' }}>
      <PageTitle level={3} style={{ fontSize: '22px', marginBottom: '16px', fontWeight: 600 }}>
        Recepción - Despacho
      </PageTitle>
      
      <StandardCard>
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          initialValues={{ motivo: 'R', fecha: dayjs() }}
        >
          {/* Primera línea: Fecha, Motivo y Producto */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px', marginBottom: '12px' }}>
            <Form.Item 
              label={<span style={{ fontSize: '13px', fontWeight: 500 }}>Fecha <span style={{ color: 'red' }}>*</span></span>}
              name="fecha" 
              style={{ marginBottom: '12px' }} 
              rules={[{ required: true, message: 'Seleccione la fecha' }]}
            > 
              <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" size="middle" />
            </Form.Item>
            <Form.Item 
              label={<span style={{ fontSize: '13px', fontWeight: 500 }}>Motivo <span style={{ color: 'red' }}>*</span></span>}
              name="motivo" 
              style={{ marginBottom: '12px' }} 
              rules={[{ required: true, message: 'Seleccione el motivo' }]}
            > 
              <Select placeholder="Seleccione motivo" onChange={handleMotivoChange} size="middle">
                {motivoOptions.map(opt => (
                  <Option key={opt.value} value={opt.value}>{opt.label}</Option>
                ))}
              </Select>
            </Form.Item>
            <Form.Item 
              label={<span style={{ fontSize: '13px', fontWeight: 500 }}>Producto <span style={{ color: 'red' }}>*</span></span>}
              name="producto" 
              style={{ marginBottom: '12px' }} 
              rules={[{ required: true, message: 'Seleccione el producto' }]}
            > 
              <Select placeholder="Seleccione producto" loading={loadingProductos} allowClear onChange={handleProductoChange} size="middle">
                {productos.map((prod: any) => (
                  <Option key={prod.id} value={prod.id}>{prod.nombre || prod.descripcion || prod.name}</Option>
                ))}
              </Select>
            </Form.Item>
          </div>

          {/* Segunda línea: Marca, Buque y Observaciones */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px', marginBottom: '12px' }}>
            <Form.Item 
              label={<span style={{ fontSize: '13px', fontWeight: 500 }}>Marca</span>}
              name="marca" 
              style={{ marginBottom: '12px' }}
            >
              <Input size="middle" />
            </Form.Item>
            <Form.Item 
              label={<span style={{ fontSize: '13px', fontWeight: 500 }}>Buque</span>}
              name="buque" 
              style={{ marginBottom: '12px' }}
            >
              <Input size="middle" />
            </Form.Item>
            <Form.Item 
              label={<span style={{ fontSize: '13px', fontWeight: 500 }}>Observaciones</span>}
              name="observaciones" 
              style={{ marginBottom: '12px' }}
            >
              <Input.TextArea rows={2} placeholder="Ingrese observaciones..." style={{ fontSize: '13px' }} />
            </Form.Item>
          </div>

          {/* Ordenes Pendientes */}
          <Form.Item 
            label={<span style={{ fontSize: '13px', fontWeight: 500 }}>Número de Orden <span style={{ color: 'red' }}>*</span></span>}
            name="orden_pendiente" 
            style={{ marginBottom: '12px' }} 
            rules={[{ required: true, message: 'Ingrese el número de orden' }]}
          >
            <Input
              type="number"
              placeholder="Ingrese el número de orden a validar"
              onChange={handleNumeroOrdenChange}
              onBlur={handleNumeroOrdenBlur}
              disabled={validandoOrden}
              suffix={validandoOrden ? <Spin size="small" /> : null}
              size="middle"
            />
          </Form.Item>

          {/* Card con datos de la orden */}
          {ordenSeleccionada && (
            <StandardCard style={{ marginBottom: '12px', backgroundColor: '#f0f8ff', borderColor: '#d6e4ff' }}>
              <div style={{ fontSize: '14px', fontWeight: 600, color: theme.colors.primary, marginBottom: '12px' }}>
                Datos de la Orden
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '12px' }}>
                <div>
                  <span style={{ fontSize: '12px', color: '#666', display: 'block', marginBottom: '4px' }}>Tipo:</span>
                  <span style={{ fontSize: '13px', fontWeight: 500, color: '#333' }}>
                    {ordenSeleccionada?.tipo_display || ordenSeleccionada?.tipo || ''}
                  </span>
                </div>
                <div>
                  <span style={{ fontSize: '12px', color: '#666', display: 'block', marginBottom: '4px' }}>Fecha:</span>
                  <span style={{ fontSize: '13px', fontWeight: 500, color: '#333' }}>
                    {ordenSeleccionada?.fecha ? new Date(ordenSeleccionada.fecha).toLocaleDateString('es-ES') : ''}
                  </span>
                </div>
                <div>
                  <span style={{ fontSize: '12px', color: '#666', display: 'block', marginBottom: '4px' }}>Salida desde:</span>
                  <span style={{ fontSize: '13px', fontWeight: 500, color: '#333' }}>
                    {ordenSeleccionada?.tipo === 'C' ? ordenSeleccionada?.contrato_vendedor_nombre : 
                     ordenSeleccionada?.tipo === 'V' ? ordenSeleccionada?.contrato_vendedor_nombre :
                     ordenSeleccionada?.tipo === 'A' ? ordenSeleccionada?.sucursal_nombre :
                     ordenSeleccionada?.origen_nombre || ''}
                  </span>
                </div>
                <div>
                  <span style={{ fontSize: '12px', color: '#666', display: 'block', marginBottom: '4px' }}>Ingreso a:</span>
                  <span style={{ fontSize: '13px', fontWeight: 500, color: '#333' }}>
                    {ordenSeleccionada?.tipo === 'C' ? ordenSeleccionada?.contrato_comprador_nombre : 
                     ordenSeleccionada?.tipo === 'V' ? ordenSeleccionada?.contrato_comprador_nombre :
                     ordenSeleccionada?.tipo === 'A' ? ordenSeleccionada?.sucursal_otra_nombre :
                     ordenSeleccionada?.tipo === 'E' ? ordenSeleccionada?.empresa_nombre :
                     ordenSeleccionada?.tipo === 'D' ? ordenSeleccionada?.empresa_nombre :
                     ordenSeleccionada?.destino_nombre || ''}
                  </span>
                </div>
              </div>
            </StandardCard>
          )}

          

          {/* Ticket de Balanza */}
          <Form.Item 
            label={<span style={{ fontSize: '13px', fontWeight: 500 }}>Ticket de Balanza <span style={{ color: 'red' }}>*</span></span>}
            name="ticket_balanza" 
            style={{ marginBottom: '12px' }} 
            rules={[{ required: true, message: 'Ingrese el número de ticket' }]}
          >
            <Input
              type="number"
              placeholder={!ordenSeleccionada ? "Primero ingrese y valide un número de orden" : "Ingrese el número de ticket a validar"}
              onChange={handleNumeroTicketChange}
              onBlur={handleNumeroTicketBlur}
              disabled={!ordenSeleccionada || validandoOrden || validandoTicket}
              suffix={validandoTicket ? <Spin size="small" /> : null}
              size="middle"
            />
          </Form.Item>

          {/* Card con datos del ticket */}
          {ticketSeleccionado && (
            <StandardCard style={{ marginBottom: '12px', backgroundColor: '#fff7e6', borderColor: '#ffd591' }}>
              <div style={{ fontSize: '14px', fontWeight: 600, color: '#fa8c16', marginBottom: '12px' }}>
                Datos del Ticket
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '12px' }}>
                <div>
                  <span style={{ fontSize: '12px', color: '#666', display: 'block', marginBottom: '4px' }}>Tipo:</span>
                  <span style={{ fontSize: '13px', fontWeight: 500, color: '#333' }}>
                    {ticketSeleccionado?.compra_servicio === 'C' ? 'Compra' :
                     ticketSeleccionado?.compra_servicio === 'A' ? 'A Otra Sucursal' :
                     ticketSeleccionado?.compra_servicio === 'E' ? 'Salida Cliente' :
                     ticketSeleccionado?.compra_servicio === 'D' ? 'Cambio Dueño Salida' :
                     ticketSeleccionado?.compra_servicio === 'F' ? 'Desde Sucursal' :
                     ticketSeleccionado?.compra_servicio === 'G' ? 'Cambio Dueño Ingreso' :
                     ticketSeleccionado?.compra_servicio === 'V' ? 'Venta' :
                     ticketSeleccionado?.compra_servicio === 'S' ? 'Servicio' :
                     ticketSeleccionado?.compra_servicio === 'B' ? 'Baja' :
                     ticketSeleccionado?.compra_servicio === 'R' ? 'Recupero' :
                     ticketSeleccionado?.compra_servicio || ''}
                  </span>
                </div>
                <div>
                  <span style={{ fontSize: '12px', color: '#666', display: 'block', marginBottom: '4px' }}>Salida a:</span>
                  <span style={{ fontSize: '13px', fontWeight: 500, color: '#333' }}>
                    {ticketSeleccionado?.cliente_nombre || ticketSeleccionado?.cliente || ticketSeleccionado?.aotrasucursal_nombre || ticketSeleccionado?.sucursal_otra_nombre || ''}
                  </span>
                </div>
                <div>
                  <span style={{ fontSize: '12px', color: '#666', display: 'block', marginBottom: '4px' }}>Chofer:</span>
                  <span style={{ fontSize: '13px', fontWeight: 500, color: '#333' }}>
                    {ticketSeleccionado?.chofer_nombre || ticketSeleccionado?.chofer || ''}
                  </span>
                </div>
                <div>
                  <span style={{ fontSize: '12px', color: '#666', display: 'block', marginBottom: '4px' }}>Patente Camión:</span>
                  <span style={{ fontSize: '13px', fontWeight: 500, color: '#333' }}>
                    {ticketSeleccionado?.camion_patente || ''}
                  </span>
                </div>
                <div>
                  <span style={{ fontSize: '12px', color: '#666', display: 'block', marginBottom: '4px' }}>Kilos Netos:</span>
                  <span style={{ fontSize: '13px', fontWeight: 500, color: '#333' }}>
                    {ticketSeleccionado?.kilos_primera && ticketSeleccionado?.kilos_segunda ? 
                      Math.abs(ticketSeleccionado.kilos_primera - ticketSeleccionado.kilos_segunda).toFixed(2) : ''}
                  </span>
                </div>
              </div>
            </StandardCard>
          )}

          
          {/* Botones de acción */}
          <div style={{ 
            display: 'flex', 
            justifyContent: 'flex-end', 
            gap: '12px', 
            marginTop: '16px',
            paddingTop: '16px',
            borderTop: `1px solid ${theme.colors.borderLight}`
          }}>
            <Button 
              type="default"
              onClick={() => {
                // Resetear el formulario pero mantener la fecha actual
                form.resetFields();
                form.setFieldsValue({ fecha: dayjs() });
                
                // Limpiar los datos desplegados
                setOrdenSeleccionada(null);
                setTicketSeleccionado(null);
                
                // Limpiar las listas de órdenes y tickets
                setOrdenesPendientes([]);
                setTicketsBalanza([]);
                
                // Limpiar el estado de validación
                setValidandoOrden(false);
                setValidandoTicket(false);
              }}
              size="middle"
              style={{ height: '32px', fontSize: '13px', fontWeight: 500, minWidth: '100px' }}
            >
              Cancelar
            </Button>
            <Button 
              type="primary" 
              onClick={() => form.submit()} 
              disabled={isSubmitting}
              loading={isSubmitting}
              size="middle"
              style={{ height: '32px', fontSize: '13px', fontWeight: 500, minWidth: '100px' }}
            >
              {isSubmitting ? 'Grabando...' : 'Grabar Todo'}
            </Button>
          </div>
        </Form>
      </StandardCard>
    </div>
  );
};

export default RecepcionDespacho;
